import _ from "lodash";
import { action, computed, observable } from "mobx";
import { observer, Observer } from "mobx-react";
import TreeNode from "primereact/treenode";
import { TreeSelect, TreeSelectProps } from "primereact/treeselect";
import React, { useContext, useEffect, useMemo } from "react";
import { QueryClient, useQueryClient } from "react-query";
import styled from "styled-components";
import { candidateAPI } from "../../../api/candidate";
import { GetDetailsForCandidateResponse } from "../../../api/candidate/getDetailsForCandidate";
import { getInputWithPanelHeightLocker } from "../../../Components/getInputWithPanelHeightLocker";
import {
  treeSelectCustomValueTemplate,
  treeSelectOnChange,
  useDefaultTreeSelectProps,
} from "../../../Components/treeSelectUtils";
import { getLocStore, useLocStore } from "../../../wsLocalization";
import { LocContext } from "../../../wsLocalization/LocContext";
import { separator } from "../../../WSUtils/keySeparator";
import { Store2 } from "../../../WSUtils/Store";
import {
  invalidateQueries,
  useLatestQuery,
} from "../../../WSUtils/useLatestQuery";
import { UserInfo } from "../../../WSUtils/UserInfo";
import { UserInfoContext } from "../../../WSUtils/UserInfoContext";
import { useStore2 } from "../../../WSUtils/useStore";
import { concatClassnames } from "../../../WSUtils/util";
import { WSFormGrid } from "../../../WSUtils/WSFormGrid";
import { sentryAssertData } from "../../../WSUtils/wsSentry";
import { CVEditContext, CVEditContextStore } from "../CVEditContext";
import {
  CVEditBaseField,
  cveField,
  cveFieldStore,
  OnRenderCvEditComponent,
} from "../Fields/CVEditBaseField";

export const CVEditPositionWishesSegment = () => {
  const L = useLocStore(LocContext.CV);
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);

  const store = useStore2(
    "CVEditPositionWishesSegmentStore",
    (dt) =>
      new CVEditPositionWishesSegmentStore({
        name: "CVEditPositionWishesSegmentStore",
        parentDt: dt,
        useHooks: (t: CVEditPositionWishesSegmentStore) => {
          t.userInfo = useContext(UserInfoContext);
          t.queryClient = useQueryClient();
          t.cvEditContext = useContext(CVEditContext);
        },
      })
  );

  const { data: details } = useLatestQuery(
    ["candidateAPI.getDetailsForCandidate"],
    () =>
      candidateAPI.getDetailsForCandidate({
        userInfo,
      })
  );

  sentryAssertData([details], { userInfo });

  useEffect(
    action(() => {
      store.details = details;
    }),
    [details]
  );

  return (
    <Observer
      render={() => (
        <CVEditPositionWishesSegmentStyles className="cve-position-wishes-segment cve-segment">
          <div className="cvp-h2 cve-header">
            {L.id("59dd12f9698f").loc("Stillingsønsker")}
          </div>
          <CVEditBaseField def={store.wantedLocationsField} />
          <CVEditBaseField def={store.wantedPositionTypesField} />
        </CVEditPositionWishesSegmentStyles>
      )}
    />
  );
};

const CVEditPositionWishesSegmentStyles = styled(WSFormGrid)``;

export class CVEditPositionWishesSegmentStore extends Store2<any> {
  declare userInfo: UserInfo;
  declare queryClient: QueryClient;
  declare cvEditContext: CVEditContextStore;

  @observable details: GetDetailsForCandidateResponse;

  getFieldDependencies = () => ({
    cvEditContext: this.cvEditContext,
    queryClient: this.queryClient,
    userInfo: this.userInfo,
    dt: this.dt,
  });

  @computed get L() {
    return getLocStore(this.userInfo, LocContext.CV);
  }

  @observable wantedPositionTypesField = cveField((getValueVersioner) => {
    const _this = this;
    const store = cveFieldStore({
      value: {},
      get validationError(): string {
        return "";
      },
    });

    const valueVersioner = getValueVersioner(
      store,
      0,
      false,
      "wantedPositionTypesField"
    );

    this.dt.disposers.push(
      valueVersioner.onNewValue(() => {
        const data = this.details;
        if (!data?.ok) return;

        return {
          valueVersion: data?.valueVersion,
          getVal: () =>
            data.nflertypeVikartype
              .split(",")
              .map((v) => Number(v.split(":")[1]))
              .reduce((acc, id) => {
                acc[id] = true;
                return acc;
              }, {}),
        };
      })
    );

    const onChange: TreeSelectProps["onChange"] = action((evt) => {
      evt.originalEvent.persist();

      const [newValue, clickedOnGroup] = treeSelectOnChange(evt);
      if (clickedOnGroup) return;

      const oldValue = store.value;

      const mutate = () => {
        const candidateAPI = this.userInfo.fullAPI.candidateAPI;

        const removed = _.difference(
          Object.keys(oldValue),
          Object.keys(newValue)
        );
        const added = _.difference(
          Object.keys(newValue),
          Object.keys(oldValue)
        );

        const removeOperations = removed.map((v) =>
          candidateAPI.removePositionWishFromCandidate({
            showSentryDialogOnError: true,
            userInfo: this.userInfo,
            positionType: Number(v),
          })
        );

        const addOperations = added.map((v) =>
          candidateAPI.addPositionWishToCandidate({
            showSentryDialogOnError: true,
            userInfo: this.userInfo,
            positionType: Number(v),
          })
        );

        const operations = [...removeOperations, ...addOperations];
        if (operations.length > 0) return Promise.all(operations);
      };

      const invalidate = () =>
        invalidateQueries(_this.queryClient, [
          "candidateAPI.getDetailsForCandidate",
        ]);

      return valueVersioner.onChange({
        newValue,
        mutateBackend: mutate,
        invalidateQuery: invalidate,
      });
    });

    return {
      store,
      id: "WantedPositionTypes",
      getLabel: () => this.L.id("07d70a726666").loc("Stillingstyper"),
      hasUnsavedChanges: () => false,
      onRenderComponent: observer<OnRenderCvEditComponent>(({ def }) => {
        const userInfo = useContext(UserInfoContext);

        const { onHide, onShow, heightLockerClassName, lockedHeight } = useMemo(
          () => getInputWithPanelHeightLocker(),
          []
        );

        const { data: options } = useLatestQuery(
          ["typesAPI.getTypes(vikartype)"],
          () =>
            userInfo.fullAPI.typesAPI.getTypes({
              userInfo,
              group: "vikartype",
              excludeFolderName: false,
            })
        );

        sentryAssertData(options, { userInfo });

        const defaultTreeSelectProps = useDefaultTreeSelectProps();

        return (
          <TreeSelect
            {...defaultTreeSelectProps}
            inputId={def.id}
            name={def.id}
            onShow={onShow}
            onHide={onHide}
            valueTemplate={treeSelectCustomValueTemplate}
            style={{
              height: lockedHeight.get(),
            }}
            className={concatClassnames([
              "cv-treeselect-grow",
              heightLockerClassName,
            ])}
            display="chip"
            options={options?.mapped || []}
            value={store.value}
            onChange={onChange}
          />
        );
      }),
    };
  });

  @observable wantedLocationsField = cveField((getValueVersioner) => {
    const _this = this;
    const store = cveFieldStore({
      value: {},
      get validationError(): string {
        return "";
      },
    });

    const valueVersioner = getValueVersioner(
      store,
      1000,
      true,
      "wantedLocationsField"
    );

    this.dt.disposers.push(
      valueVersioner.onNewValue(() => {
        const data = this.details;
        if (!data?.ok) return;

        return {
          valueVersion: data?.valueVersion,
          getVal: () =>
            data.flerIdKommuner
              .split(",")
              .map((id) => Number(id))
              .reduce((acc, id) => {
                acc[id] = true;
                return acc;
              }, {}),
        };
      })
    );

    const onChange: TreeSelectProps["onChange"] = action((evt) => {
      const [newValue, clickedOnGroup] = treeSelectOnChange(evt);
      if (clickedOnGroup) return;

      const mutate = () =>
        this.userInfo.fullAPI.candidateAPI.updateCandidate({
          showSentryDialogOnError: true,
          userInfo: this.userInfo,
          fields: [
            {
              fieldName: "flerIdKommuner",
              fieldValue: Object.keys(newValue).join(","),
            },
          ],
        });

      const invalidate = () =>
        invalidateQueries(_this.queryClient, [
          "candidateAPI.getDetailsForCandidate",
        ]);

      return valueVersioner.onChange({
        newValue,
        mutateBackend: mutate,
        invalidateQuery: invalidate,
      });
    });

    return {
      store,
      id: "WantedLocations",
      getLabel: () => this.L.id("9b9186bdbfbc").loc("Arbeidssted"),
      hasUnsavedChanges: () => false,
      onRenderComponent: observer<OnRenderCvEditComponent>(({ def }) => {
        const userInfo = useContext(UserInfoContext);

        const { onHide, onShow, heightLockerClassName, lockedHeight } = useMemo(
          () => getInputWithPanelHeightLocker(),
          []
        );

        const { data: municipalities } = useLatestQuery(
          ["placesAPI.getMunicipalities"],
          () =>
            userInfo.fullAPI.placesAPI.getMunicipalities({
              userInfo,
            })
        );

        const { data: counties } = useLatestQuery(
          ["placesAPI.getCounties"],
          () =>
            userInfo.fullAPI.placesAPI.getCounties({
              userInfo,
            })
        );

        sentryAssertData([municipalities, counties], { userInfo });

        const options = useMemo(() => {
          if (!counties || !municipalities) return [];

          const countiesNodes: { [id: string]: TreeNode } = {};

          counties.items.forEach((c) => {
            countiesNodes[c.id] = {
              key: `GROUP${separator}${c.id}`,
              children: [],
              label: c.navn,
            };
          });

          municipalities.items.forEach((m) => {
            const county = countiesNodes[m.id_fylker];

            county.children.push({
              key: m.id,
              children: null,
              label: m.navn,
            });
          });

          return Object.values(countiesNodes);
        }, [municipalities, counties]);

        const defaultTreeSelectProps = useDefaultTreeSelectProps();

        return (
          <TreeSelect
            {...defaultTreeSelectProps}
            inputId={def.id}
            name={def.id}
            onShow={onShow}
            onHide={onHide}
            valueTemplate={treeSelectCustomValueTemplate}
            style={{
              height: lockedHeight.get(),
            }}
            className={concatClassnames([
              "cv-treeselect-grow",
              heightLockerClassName,
            ])}
            display="chip"
            options={options || []}
            value={store.value}
            onChange={onChange}
          />
        );
      }),
    };
  });
}
